/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import tokens from "@epic-tokens/eds-web";
import { parse as parseGradient } from "gradient-parser";
import type { ColorStop, GradientNode } from "gradient-parser";
import { FC, memo, useMemo } from "react";
import {
	EdsCSSGradient,
	EdsGradientType,
	EdsGradientTypeIds,
	EdsIconGradientProps,
	GradientProps,
	InferPropType,
	Positions,
} from "./EdsIcon.type";

export const isValidEdsCSSGradient = (value: string): value is EdsCSSGradient => {
	const gradientRegex = /^linear-gradient\(([^)]+)\)$/;
	return gradientRegex.test(value);
};

export const edsGradientTypes = Object.keys(tokens.gradient) as EdsGradientType[];

const positionsForOrientation = (orientation?: InferPropType<GradientNode, "orientation">): Positions => {
	const positions: Positions = { x1: "0%", x2: "0%", y1: "0%", y2: "0%" };

	if (!orientation) return positions;
	if (orientation instanceof Array) return positions;

	if (orientation.type === "angular") {
		const anglePI = Number(orientation.value) * (Math.PI / 180);
		positions.x1 = `${Math.round(50 + Math.sin(anglePI + Math.PI) * 50)}%`;
		positions.y1 = `${Math.round(50 + Math.cos(anglePI) * 50)}%`;
		positions.x2 = `${Math.round(50 + Math.sin(anglePI) * 50)}%`;
		positions.y2 = `${Math.round(50 + Math.cos(anglePI + Math.PI) * 50)}%`;
		return positions;
	}

	if (orientation.type === "directional") {
		if (orientation.value.includes("left")) {
			positions.x1 = "100%";
		} else if (orientation.value.includes("right")) {
			positions.x2 = "100%";
		}
		if (orientation.value.includes("top")) {
			positions.y1 = "100%";
		} else if (orientation.value.includes("bottom")) {
			positions.y2 = "100%";
		}
	}

	return positions;
};

const getColorStops = (colorStop: ColorStop, index: number, arr: ColorStop[]): React.ReactElement => {
	const offset = `${(index / (arr.length - 1)) * 100}%`;
	let stopColor = "rgb(0,0,0)";
	let stopOpacity = 1.0;

	switch (colorStop.type) {
		case "rgb": {
			const [r, g, b] = colorStop.value;
			stopColor = `rgb(${r},${g},${b})`;
			break;
		}
		case "rgba": {
			const [r, g, b, a] = colorStop.value;
			stopColor = `rgb(${r},${g},${b})`;
			stopOpacity = Number(a);
			break;
		}
		case "hex": {
			stopColor = `#${colorStop.value}`;
			break;
		}
		case "literal": {
			stopColor = colorStop.value;
			break;
		}
	}

	return <stop key={index} offset={offset} style={{ stopColor, stopOpacity }} />;
};

export const tokenToGradientId = (token: EdsGradientType): EdsGradientTypeIds => `eds-icon-gradient-${token}`;

export const GradientSVG: FC<GradientProps> = memo(({ css, token, ...props }) => {
	const { type, orientation, colorStops } = useMemo(() => {
		const [gradient] = parseGradient(css);
		return gradient;
	}, [css]);

	const { x1, x2, y1, y2 } = useMemo(() => positionsForOrientation(orientation), [orientation]);

	if (type !== "linear-gradient") return "";
	return (
		<svg xmlns="http://www.w3.org/2000/svg" {...props}>
			<defs>
				<linearGradient id={tokenToGradientId(token)} x1={x1} y1={y1} x2={x2} y2={y2}>
					{colorStops.map(getColorStops)}
				</linearGradient>
			</defs>
		</svg>
	);
});

GradientSVG.displayName = "GradientSVG";

const EdsIconGradient: FC<EdsIconGradientProps> = memo(({ token, ...props }) => {
	if (!token) return "";

	const css = tokens.gradient[token];
	if (!css) return "";

	if (!isValidEdsCSSGradient(css)) return "";
	return (
		<GradientSVG
			css={css}
			token={token}
			width="0"
			height="0"
			style={{ position: "absolute", visibility: "hidden", zIndex: "-1", ...props.style }}
			{...props}
		/>
	);
});

EdsIconGradient.displayName = "EdsIconGradient";

export default EdsIconGradient;
